'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { productsApi, favoritesApi } from '@/lib/api';
import { Product, ProductOption, OptionValue, DisplayMode, SelectedOption, CartItem } from '@/types';
import { getImageUrl } from '@/lib/utils';
import SaveToFavoritesModal from './SaveToFavoritesModal';

interface EditCartItemModalProps {
  cartItem: CartItem;
  cartIndex: number;
  onSave: (index: number, item: CartItem) => void;
  onClose: () => void;
}

export default function EditCartItemModal({ cartItem, cartIndex, onSave, onClose }: EditCartItemModalProps) {
  const router = useRouter();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(cartItem.quantity);
  const [specialInstructions, setSpecialInstructions] = useState(cartItem.specialInstructions || '');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [modalOption, setModalOption] = useState<ProductOption | null>(null);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('amam_token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    loadProduct();
  }, [cartItem.productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await productsApi.getProduct(cartItem.productId);
      setProduct(response.data);

      // Initialize options from cart item
      const defaults: Record<string, any> = {};
      response.data.options?.forEach((option: ProductOption) => {
        // Find if this option was selected in the cart item
        const cartOption = cartItem.selectedOptions.find(
          (so) => so.optionId === option._id || so.optionName === option.name
        );

        if (option.displayMode === DisplayMode.CHECKMARK) {
          defaults[option._id!] = !!cartOption;
        } else if (option.displayMode === DisplayMode.PLUS_MINUS_LIST) {
          defaults[option._id!] = {};
          option.values.forEach(val => {
            // Find matching cart option with quantity
            const matchingCartOption = cartItem.selectedOptions.find(
              (so) =>
                (so.optionId === option._id || so.optionName === option.name) &&
                (so.selectedValue === val.name || so.selectedValue === val.value)
            );
            defaults[option._id!][val._id!] = matchingCartOption?.quantity || 0;
          });
        } else if (option.displayMode === DisplayMode.INLINE_OPTIONS || option.displayMode === DisplayMode.MODAL_SELECT) {
          if (cartOption) {
            // Find the matching value by name
            const matchingValue = option.values.find(
              v => v.name === cartOption.selectedValue || v.nameHe === cartOption.selectedValueHe
            );
            if (matchingValue) {
              defaults[option._id!] = matchingValue._id;
            }
          } else {
            // Use default value if no cart option
            const defaultValue = option.values.find(v => v.isDefault);
            if (defaultValue) {
              defaults[option._id!] = defaultValue._id;
            }
          }
        } else if (option.displayMode === DisplayMode.TEXT_INPUT || option.displayMode === DisplayMode.TEXT_AREA) {
          defaults[option._id!] = cartOption?.textValue || '';
        }
      });
      setSelectedOptions(defaults);
    } catch (error) {
      console.error('Failed to load product:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPrice = () => {
    if (!product) return 0;

    let total = product.price;

    product.options?.forEach(option => {
      const selected = selectedOptions[option._id!];

      if (option.displayMode === DisplayMode.CHECKMARK && selected) {
        const value = option.values[0];
        total += value?.priceModifier || 0;
      } else if (option.displayMode === DisplayMode.INLINE_OPTIONS || option.displayMode === DisplayMode.MODAL_SELECT) {
        const value = option.values.find(v => v._id === selected);
        total += value?.priceModifier || 0;
      } else if (option.displayMode === DisplayMode.PLUS_MINUS_LIST) {
        Object.keys(selected || {}).forEach(valueId => {
          const qty = selected[valueId];
          const value = option.values.find(v => v._id === valueId);
          total += (value?.priceModifier || 0) * qty;
        });
      }
    });

    return total * quantity;
  };

  const validateOptions = () => {
    const newErrors: Record<string, string> = {};

    product?.options?.forEach(option => {
      if (option.required) {
        const selected = selectedOptions[option._id!];

        if (option.displayMode === DisplayMode.TEXT_INPUT || option.displayMode === DisplayMode.TEXT_AREA) {
          if (!selected || selected.trim() === '') {
            newErrors[option._id!] = `${option.nameHe || option.name} הינו שדה חובה`;
          }
        } else if (option.displayMode === DisplayMode.INLINE_OPTIONS || option.displayMode === DisplayMode.MODAL_SELECT) {
          if (!selected) {
            newErrors[option._id!] = `יש לבחור ${option.nameHe || option.name}`;
          }
        } else if (option.displayMode === DisplayMode.PLUS_MINUS_LIST && option.minCount) {
          const total = Object.values(selected || {}).reduce((sum: number, qty) => sum + (qty as number), 0);
          if (total < option.minCount) {
            newErrors[option._id!] = `יש לבחור לפחות ${option.minCount} פריטים`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateOptions() || !product) return;

    const cartSelectedOptions: SelectedOption[] = [];

    product.options?.forEach(option => {
      const selected = selectedOptions[option._id!];

      if (option.displayMode === DisplayMode.CHECKMARK) {
        if (selected) {
          const value = option.values[0];
          cartSelectedOptions.push({
            optionId: option._id,
            optionName: option.name,
            optionNameHe: option.nameHe,
            displayMode: option.displayMode,
            selectedValue: value.name || value.value,
            selectedValueHe: value.nameHe || value.valueHe,
            priceModifier: value.priceModifier,
          });
        }
      } else if (option.displayMode === DisplayMode.INLINE_OPTIONS || option.displayMode === DisplayMode.MODAL_SELECT) {
        const value = option.values.find(v => v._id === selected);
        if (value) {
          cartSelectedOptions.push({
            optionId: option._id,
            optionName: option.name,
            optionNameHe: option.nameHe,
            displayMode: option.displayMode,
            selectedValue: value.name || value.value,
            selectedValueHe: value.nameHe || value.valueHe,
            priceModifier: value.priceModifier,
          });
        }
      } else if (option.displayMode === DisplayMode.PLUS_MINUS_LIST) {
        Object.keys(selected || {}).forEach(valueId => {
          const qty = selected[valueId];
          if (qty > 0) {
            const value = option.values.find(v => v._id === valueId);
            if (value) {
              cartSelectedOptions.push({
                optionId: option._id,
                optionName: option.name,
                optionNameHe: option.nameHe,
                displayMode: option.displayMode,
                selectedValue: value.name || value.value,
                selectedValueHe: value.nameHe || value.valueHe,
                quantity: qty,
                priceModifier: value.priceModifier,
              });
            }
          }
        });
      } else if (option.displayMode === DisplayMode.TEXT_INPUT || option.displayMode === DisplayMode.TEXT_AREA) {
        if (selected) {
          cartSelectedOptions.push({
            optionId: option._id,
            optionName: option.name,
            optionNameHe: option.nameHe,
            displayMode: option.displayMode,
            textValue: selected,
            priceModifier: 0,
          });
        }
      }
    });

    const updatedCartItem: CartItem = {
      productId: product._id,
      productName: product.name,
      productNameHe: product.nameHe,
      productImage: product.image,
      quantity,
      unitPrice: calculateTotalPrice() / quantity,
      selectedOptions: cartSelectedOptions,
      specialInstructions,
      itemTotal: calculateTotalPrice(),
    };

    onSave(cartIndex, updatedCartItem);
    onClose();
  };

  const handleSaveToFavorites = () => {
    if (!isLoggedIn) {
      alert('יש להתחבר כדי לשמור מועדפים');
      onClose();
      router.push('/login');
      return;
    }
    setShowFavoritesModal(true);
  };

  function renderOptionInput(option: ProductOption) {
    switch (option.displayMode) {
      case DisplayMode.INLINE_OPTIONS:
        return (
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {option.values.map(value => (
              <button
                key={value._id}
                role="radio"
                aria-checked={selectedOptions[option._id!] === value._id}
                onClick={() => setSelectedOptions({
                  ...selectedOptions,
                  [option._id!]: value._id,
                })}
                className={`px-4 py-3 rounded-lg border-2 transition ${
                  selectedOptions[option._id!] === value._id
                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                    : 'border-gray-200 hover:border-blue-300'
                } ${value.image ? 'flex flex-col items-center' : 'flex items-center justify-center min-h-[60px]'}`}
              >
                {value.image ? (
                  <>
                    <div className="w-24 h-24 flex items-center justify-center bg-gray-50 rounded mb-2">
                      <img
                        src={getImageUrl(value.image)}
                        alt={value.nameHe || value.name}
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    </div>
                    <div className="text-center">
                      <div className="font-medium">{value.nameHe || value.name}</div>
                      {value.priceModifier !== 0 && (
                        <div className="text-sm mt-1">
                          ({value.priceModifier > 0 ? '+' : ''}{value.priceModifier.toFixed(2)})
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    <div className="font-medium">{value.nameHe || value.name}</div>
                    {value.priceModifier !== 0 && (
                      <div className="text-sm mt-1">
                        ({value.priceModifier > 0 ? '+' : ''}{value.priceModifier.toFixed(2)})
                      </div>
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        );

      case DisplayMode.MODAL_SELECT:
        const selectedValue = option.values.find(v => v._id === selectedOptions[option._id!]);
        return (
          <button
            onClick={() => setModalOption(option)}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-right hover:border-blue-500 transition"
          >
            {selectedValue ? (
              <span>
                {selectedValue.nameHe || selectedValue.name}
                {selectedValue.priceModifier !== 0 && (
                  <span className="mr-2 text-gray-600">
                    ({selectedValue.priceModifier > 0 ? '+' : ''}{selectedValue.priceModifier.toFixed(2)})
                  </span>
                )}
              </span>
            ) : (
              <span className="text-gray-400">{option.placeholderHe || option.placeholder || 'בחר אפשרות'}</span>
            )}
          </button>
        );

      case DisplayMode.CHECKMARK:
        const checkValue = option.values[0];
        return (
          <label className={`cursor-pointer ${checkValue.image ? 'flex items-start gap-4' : 'flex items-center gap-3'}`}>
            {checkValue.image ? (
              <div className="flex-shrink-0">
                <div className="w-32 h-32 flex items-center justify-center bg-gray-50 rounded mb-2">
                  <img
                    src={getImageUrl(checkValue.image)}
                    alt={checkValue.nameHe || checkValue.name}
                    className="max-w-full max-h-full object-contain rounded"
                  />
                </div>
                <div className="text-center">
                  <div className="font-medium">{checkValue.nameHe || checkValue.name}</div>
                  {checkValue.priceModifier !== 0 && (
                    <div className="text-gray-600 text-sm mt-1">
                      ({checkValue.priceModifier > 0 ? '+' : ''}{checkValue.priceModifier.toFixed(2)})
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span className="flex items-center gap-3">
                {checkValue.nameHe || checkValue.name}
                {checkValue.priceModifier !== 0 && (
                  <span className="text-gray-600">
                    ({checkValue.priceModifier > 0 ? '+' : ''}{checkValue.priceModifier.toFixed(2)})
                  </span>
                )}
              </span>
            )}
            <input
              type="checkbox"
              checked={selectedOptions[option._id!] || false}
              onChange={(e) => setSelectedOptions({
                ...selectedOptions,
                [option._id!]: e.target.checked,
              })}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mt-1"
            />
          </label>
        );

      case DisplayMode.PLUS_MINUS_LIST:
        return (
          <div className="space-y-3">
            {option.values.map(value => {
              const qty = selectedOptions[option._id!]?.[value._id!] || 0;
              return (
                <div key={value._id} className="flex items-center justify-between">
                  {value.image ? (
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 flex items-center justify-center bg-gray-50 rounded mb-1">
                          <img
                            src={getImageUrl(value.image)}
                            alt={value.nameHe || value.name}
                            className="max-w-full max-h-full object-contain rounded"
                          />
                        </div>
                        <div className="text-center">
                          <div className="font-medium">{value.nameHe || value.name}</div>
                          {value.priceModifier !== 0 && (
                            <div className="text-gray-600 text-sm">
                              ({value.priceModifier > 0 ? '+' : ''}{value.priceModifier.toFixed(2)})
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span>
                        {value.nameHe || value.name}
                        {value.priceModifier !== 0 && (
                          <span className="mr-2 text-gray-600 text-sm">
                            ({value.priceModifier > 0 ? '+' : ''}{value.priceModifier.toFixed(2)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const newQty = Math.max(0, qty - 1);
                        setSelectedOptions({
                          ...selectedOptions,
                          [option._id!]: {
                            ...selectedOptions[option._id!],
                            [value._id!]: newQty,
                          },
                        });
                      }}
                      className="w-8 h-8 bg-gray-100 rounded-lg font-bold hover:bg-gray-200"
                      aria-label={`הסר ${value.nameHe || value.name}`}
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-semibold" aria-live="polite">
                      {qty}
                    </span>
                    <button
                      onClick={() => {
                        const newQty = qty + 1;
                        if (!option.maxCount || newQty <= option.maxCount) {
                          setSelectedOptions({
                            ...selectedOptions,
                            [option._id!]: {
                              ...selectedOptions[option._id!],
                              [value._id!]: newQty,
                            },
                          });
                        }
                      }}
                      className="w-8 h-8 bg-gray-100 rounded-lg font-bold hover:bg-gray-200"
                      aria-label={`הוסף ${value.nameHe || value.name}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case DisplayMode.TEXT_INPUT:
        return (
          <input
            type="text"
            value={selectedOptions[option._id!] || ''}
            onChange={(e) => setSelectedOptions({
              ...selectedOptions,
              [option._id!]: e.target.value,
            })}
            placeholder={option.placeholderHe || option.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            aria-label={option.nameHe || option.name}
          />
        );

      case DisplayMode.TEXT_AREA:
        return (
          <textarea
            value={selectedOptions[option._id!] || ''}
            onChange={(e) => setSelectedOptions({
              ...selectedOptions,
              [option._id!]: e.target.value,
            })}
            placeholder={option.placeholderHe || option.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={3}
            aria-label={option.nameHe || option.name}
          />
        );

      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-gray-600 mb-4">לא ניתן לטעון את המוצר</p>
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            סגור
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold">עריכת {product.nameHe || product.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Product Image & Info */}
            <div className="flex gap-4 items-start">
              <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {product.image ? (
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.nameHe || product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl">🍽️</span>
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {product.nameHe || product.name}
                </h3>
                {product.descriptionHe && (
                  <p className="text-sm text-gray-600">{product.descriptionHe}</p>
                )}
                <p className="text-blue-600 font-semibold mt-1">₪{product.price.toFixed(2)}</p>
              </div>
            </div>

            {/* Options */}
            {product.options && product.options.length > 0 && (
              <div className="space-y-6">
                {product.options.map(option => (
                  <div key={option._id} className="border-b last:border-b-0 pb-4 last:pb-0">
                    <label className="block text-base font-semibold text-gray-900 mb-3">
                      {option.nameHe || option.name}
                      {option.required && <span className="text-red-600 mr-1">*</span>}
                    </label>

                    {renderOptionInput(option)}

                    {errors[option._id!] && (
                      <p className="text-red-600 text-sm mt-2">{errors[option._id!]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Special Instructions */}
            <div>
              <label htmlFor="edit-instructions" className="block text-base font-semibold text-gray-900 mb-2">
                הערות מיוחדות
              </label>
              <textarea
                id="edit-instructions"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="הוסף הערות למטבח (אופציונלי)"
              />
            </div>

            {/* Quantity */}
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold">כמות:</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-gray-100 rounded-lg font-bold hover:bg-gray-200"
                >
                  -
                </button>
                <span className="text-xl font-bold w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 bg-gray-100 rounded-lg font-bold hover:bg-gray-200"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
            >
              ביטול
            </button>
            <button
              onClick={handleSaveToFavorites}
              className="px-4 py-3 border-2 border-red-400 text-red-500 rounded-lg font-medium hover:bg-red-50 transition"
              title="שמור למועדפים"
            >
              ❤️
            </button>
            <button
              onClick={handleSave}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              עדכן - ₪{calculateTotalPrice().toFixed(2)}
            </button>
          </div>
        </div>
      </div>

      {/* Save to Favorites Modal */}
      {product && (
        <SaveToFavoritesModal
          isOpen={showFavoritesModal}
          onClose={() => setShowFavoritesModal(false)}
          productId={product._id}
          productName={product.name}
          productNameHe={product.nameHe}
          selectedOptions={selectedOptions}
          specialInstructions={specialInstructions}
        />
      )}

      {/* Nested Modal for modal-select options */}
      {modalOption && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{modalOption.nameHe || modalOption.name}</h2>
              <button
                onClick={() => setModalOption(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {modalOption.values.map(value => (
                <button
                  key={value._id}
                  onClick={() => {
                    setSelectedOptions({
                      ...selectedOptions,
                      [modalOption._id!]: value._id,
                    });
                    setModalOption(null);
                  }}
                  className={`p-4 border-2 rounded-lg transition ${
                    selectedOptions[modalOption._id!] === value._id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  } ${value.image ? 'flex flex-col items-center' : 'flex items-center justify-center min-h-[100px]'}`}
                >
                  {value.image ? (
                    <>
                      <div className="mb-3 h-48 flex items-center justify-center bg-gray-50 rounded">
                        <img
                          src={getImageUrl(value.image)}
                          alt={value.nameHe || value.name}
                          className="max-w-full max-h-full object-contain rounded"
                        />
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">{value.nameHe || value.name}</div>
                        {value.priceModifier !== 0 && (
                          <div className="text-sm text-gray-600 mt-1">
                            {value.priceModifier > 0 ? '+' : ''}₪{value.priceModifier.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <div className="font-semibold">{value.nameHe || value.name}</div>
                      {value.priceModifier !== 0 && (
                        <div className="text-sm text-gray-600 mt-1">
                          {value.priceModifier > 0 ? '+' : ''}₪{value.priceModifier.toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
